import React, { useEffect, useLayoutEffect, useMemo } from 'react';
import { Decorator, Input, Tooltip } from '@myhealth-raccoon/components';
import { ControlledSelect as Select } from '@myhealth-raccoon/forms';
import { HelpCircle, User01 } from '@untitled-ui/icons-react';
import classNames from 'classnames';
import { useForm } from 'react-hook-form';

import { AppointmentType } from 'src/services/availability';
import { BookingUserProfileModel as Doctor } from 'src/services/booking';
import DoctorIcon from 'src/assets/doctor.svg?react';
import { CheckboxItem } from 'src/components/CheckboxItem';
import { validation } from '@myhealth-raccoon/forms';
import { isFaceToFace, newPatientRegex } from 'src/utils/appointmentTypes';
import { doctorName } from 'src/utils/name';

export type ClinicBookingInformationForm = {
    patientType: "existing" | "new",
    appointmentMode: "faceToFace" | "telehealth",
    appointmentTypeId: string,
    doctorId: string | "any",
}

type Props = {
    className?: string;
    appointmentTypes: AppointmentType[],
    doctors: Doctor[],
    onNewValues?(values: ClinicBookingInformationForm): void;
    onValidate?(valid: boolean): void;
    defaultValues?: Partial<ClinicBookingInformationForm>
}

function filterAppointmentTypes(appointmentTypes: AppointmentType[],
    patientType: "existing" | "new",
    appointmentMode: "faceToFace" | "telehealth",
) {
    return appointmentTypes.filter((apt) =>
        (isFaceToFace(apt) === (appointmentMode === "faceToFace"))
        && (apt.newPatient || (patientType === "existing"))
        && ((patientType !== "existing") || !apt.appointmentName.match(newPatientRegex))
    )
}
function filterDoctors(doctors: Doctor[],
    appointmentType: AppointmentType | undefined,
    patientType: "existing" | "new",
) {
    const docs = doctors.filter(doc =>
        !appointmentType?.appointmentTypeId || doc.apptTypeIds.includes(Number(appointmentType.appointmentTypeId))
        && (patientType === "existing" || !doc.noNewPatient)
    );
    if (docs.length <= 1 || appointmentType?.displayOnly === "1") {
        return docs;
    }
    return [
        {
            userId: "any",
            automedDisplayName: "Any Doctor",
            firstname: "",
            surname: "",
            sortOrder: -1,
            automedProfileUrl: '',
            umbracoProfileUrl: ''
        },
        ...docs]
}

function doctorSelectLabel(d: Doctor) {
    const icon = d.umbracoProfileUrl ? `${import.meta.env.VITE_MYHEALTH_WEBSITE}${d.umbracoProfileUrl}` : d.automedProfileUrl;
    const img = icon
        ? <img className="size-6 mr-2 rounded-full object-fit-cover border border-gray-300" src={icon} />
        : <User01 className="size-5 ml-0.5 mr-2.5 text-gray-500" />
        ;
    return <div className="flex w-full items-center flex-row ">
        {img}
        <span className="flex-1">{doctorName(d)}</span>
    </div>
}
const emptyArray: unknown[] = [];
export function BookWithClinicForm({
    className,
    appointmentTypes = emptyArray as AppointmentType[],
    doctors = emptyArray as Doctor[],
    onNewValues,
    onValidate,
    defaultValues
}: Props) {

    const {
        handleSubmit: innerSubmit,
        register,
        control,
        formState: { errors, dirtyFields },
        watch,
        setValue,
        trigger,
    } = useForm<ClinicBookingInformationForm>({
        defaultValues,
        shouldFocusError: false,
    });

    const patientType = watch('patientType');
    const appointmentMode = watch('appointmentMode');
    const appointmentTypeId = watch('appointmentTypeId');
    const doctorId = watch('doctorId');

    const filteredAppointmentTypes = useMemo(
        () => filterAppointmentTypes(appointmentTypes, patientType, appointmentMode),
        [appointmentTypes, patientType, appointmentMode]
    );

    const selectedAppointmentType = useMemo(
        () => filteredAppointmentTypes.find(apt => apt.appointmentTypeId === appointmentTypeId),
        [filteredAppointmentTypes, appointmentTypeId]
    );

    const filteredDoctors = useMemo(
        () => filterDoctors(doctors, selectedAppointmentType, patientType)
            .sort((docA, docB) => {
                if (docA.sortOrder !== docB.sortOrder) {
                    return docA.sortOrder - docB.sortOrder
                }
                return docA.surname > docB.surname
                    ? 1
                    : docA.surname < docB.surname ? -1 : 0
            }),
        [doctors, selectedAppointmentType, patientType]
    );

    const selectedDoctor = useMemo(
        () => filteredDoctors.find(doc => doc.userId === doctorId),
        [filteredDoctors, doctorId]
    );

    useEffect(() => {
        // if the currently selected appointment type doesn't match filters
        if (!selectedAppointmentType) {
            setValue("appointmentTypeId", filteredAppointmentTypes.length === 1 ? filteredAppointmentTypes[0].appointmentTypeId : null!);
        }
    }, [selectedAppointmentType, filteredAppointmentTypes, setValue]);

    useEffect(() => {
        // if the currently selected doctor isn't available
        if (!selectedDoctor) {
            setValue("doctorId", filteredDoctors.length === 1 ? filteredDoctors[0].userId : null!);
        }
    }, [selectedDoctor, setValue, filteredDoctors]);

    useEffect(() => {
        trigger('appointmentTypeId');
        trigger('doctorId')
    }, [patientType, appointmentMode, doctorId, appointmentTypeId, trigger]);

    register("appointmentTypeId", { validate: validation.required("Please select an appointment type") });
    register("doctorId", { validate: validation.required("Please select a provider") });

    useLayoutEffect(() => {
        onValidate?.(!!selectedDoctor && !!selectedAppointmentType);
    }, [selectedDoctor, selectedAppointmentType, onValidate]);

    useEffect(() => {
        if (onNewValues) {
            innerSubmit(onNewValues)();
        }
    }, [patientType, appointmentMode, appointmentTypeId, doctorId, onNewValues, innerSubmit]);

    return <form className={classNames('space-y-4', className)}>
        <Decorator
            fieldset
            fieldError={errors.patientType}
        >
            <div className="flex flex-row gap-3">
                <CheckboxItem
                    {...register("patientType")}
                    type='radio'
                    id="patientType_existing"
                    value="existing"
                >
                    Existing patient
                </CheckboxItem>
                <CheckboxItem
                    {...register("patientType")}
                    type='radio'
                    id="patientType_new"
                    value="new"
                >
                    <span className="hidden sm:flex">New patient at this centre</span>
                    <span className='sm:hidden'>New patient</span>
                </CheckboxItem>
            </div>
        </Decorator>
        <Decorator
            fieldset
            fieldError={errors.appointmentMode}
        >
            <div className="flex flex-row gap-3">
                <CheckboxItem
                    {...register("appointmentMode")}
                    type='radio'
                    id="appointmentMode_faceToFace"
                    value="faceToFace"
                >
                    Face to Face
                </CheckboxItem>
                <CheckboxItem
                    {...register("appointmentMode")}
                    type='radio'
                    id="appointmentMode_telehealth"
                    value="telehealth"
                >
                    Telehealth{' '}
                    <Tooltip
                        tooltipContent="Telehealth allows you to consult a healthcare provider by phone or a video call."
                        attachmentPoint='top'
                        className='inline-block relative top-0.5'
                    >
                        <HelpCircle className='w-4 h-4 text-gray-400' />
                    </Tooltip>
                </CheckboxItem>
            </div>
        </Decorator>
        {filteredAppointmentTypes.length > 1
            ? <Select
                control={control}
                rules={{ validate: validation.required("Please select an appointment type") }}
                name='appointmentTypeId'
                id='appointmentType'
                fieldError={errors.appointmentTypeId}
                options={filteredAppointmentTypes}
                labelSelector={apt => apt.appointmentName.replace(newPatientRegex, "")}
                keyProperty='appointmentTypeId'
                valueProperty='appointmentTypeId'
                icon={DoctorIcon}
                placeholder='Select an appointment'
                hint={selectedAppointmentType && !isFaceToFace(selectedAppointmentType) && "Please note that to access GP Telehealth services, you must have had an face-to-face consultation within the past 12 months."}
            />
            : <Input
                disabled
                value={selectedAppointmentType?.appointmentName}
                placeholder="Select an appointment"
                fieldError={errors.appointmentTypeId}
                // error & errorMessage take priority over fieldError if specified
                errorMessage={
                    (appointmentMode === "telehealth" && patientType === "new") ? "GP Telehealth services are only available to existing patients who've had a face-to-face visit in the past 12 months."
                        : filteredAppointmentTypes.length === 0 ? "There are no available appointment types for your selection."
                            : undefined
                }
                error={filteredAppointmentTypes.length === 0}
                icon={DoctorIcon}
            />}
        {filteredDoctors.length > 1
            ? <Select
                control={control}
                rules={{ validate: validation.required("Please select a doctor") }}
                name='doctorId'
                id='doctorId'
                fieldError={dirtyFields.doctorId ? errors.doctorId : undefined}
                options={filteredDoctors}
                labelSelector={doctorSelectLabel}
                keyProperty='userId'
                valueProperty='userId'
                placeholder='Choose a doctor'
            />
            : <Input
                disabled
                value={doctorName(selectedDoctor as Doctor)}
                placeholder='Choose a doctor'
                fieldError={errors.doctorId}
                // error & errorMessage take priority over fieldError if specified
                errorMessage={filteredDoctors.length === 0
                    ? patientType === "new"
                        ? "There are no providers currently accepting new patients for this appointment type."
                        : "There are no providers currently available for this appointment type."
                    : undefined}
                error={filteredDoctors.length === 0}
                icon={User01}
            />}
    </form>
}